import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import type { User } from '../../../types';
import type { ChangeWorkGroup } from '../../../types/changes';
import type { WorkflowCustomField } from '../../../types';
import {
  hydrateIncidentCustomFieldValues,
  pickIncidentFieldValues,
  serializeIncidentCustomFieldDefaults,
  validateIncidentTicketFields,
  type IncidentCustomFieldValues,
} from '../incidentTicketFields';
import { pickFirstContactGroup } from '../incidentQuickSend';
import { useIncidentFirstContactGroups } from '../hooks/useIncidentFirstContactGroups';
import { useIncidentTargets } from '../hooks/useIncidentTargets';
import IncidentNovoTicketInfoFields from './IncidentNovoTicketInfoFields';
import IncidentTicketFieldsForm from './IncidentTicketFieldsForm';

export interface IncidentTargetOption {
  id: number;
  name: string;
}

export interface IncidentWorkflowOption {
  id: number;
  name: string;
  group_ids: number[];
}

export interface IncidentGroupDraft {
  open_in_incidents: boolean;
  incidents_group_id?: number;
  incidents_workflow_id?: number;
  prompt_on_create: boolean;
  incident_attachment_allowed: boolean;
  incident_title_default: string;
  incident_description_default: string;
  incident_urgency_default: string;
  incident_custom_field_defaults: Record<string, unknown>;
  incident_kind: '' | 'si' | 'infra_nuvem' | 'infra_hosting';
}

interface Props {
  group: ChangeWorkGroup | null;
  targets: {
    groups: IncidentTargetOption[];
    workflows: IncidentWorkflowOption[];
  };
  saving: boolean;
  onHide: () => void;
  onSave: (payload: IncidentGroupDraft) => Promise<void> | void;
  users?: Array<Pick<User, 'id' | 'name' | 'email'>>;
  embedded?: boolean;
  initialDraft?: IncidentGroupDraft | null;
  initialCustomFieldValues?: IncidentCustomFieldValues;
  initialCustomFields?: WorkflowCustomField[];
  onDraftChange?: (
    draft: IncidentGroupDraft,
    extras: {
      customFieldValues: IncidentCustomFieldValues;
      customFields: WorkflowCustomField[];
    },
  ) => void;
  /** Revisão/disparo de subtarefa: papel SI/Infra é do cadastro do grupo, não desta tela. */
  hideIncidentKind?: boolean;
}

const emptyDraft = (group: ChangeWorkGroup | null): IncidentGroupDraft => ({
  open_in_incidents: true,
  incidents_group_id: group?.incidents_group_id || undefined,
  incidents_workflow_id: group?.incidents_workflow_id || undefined,
  prompt_on_create: true,
  incident_attachment_allowed: group?.incident_attachment_allowed !== false,
  incident_title_default: group?.incident_title_default || '',
  incident_description_default: group?.incident_description_default || '',
  incident_urgency_default: group?.incident_urgency_default || '',
  incident_custom_field_defaults: group?.incident_custom_field_defaults || {},
  incident_kind:
    group?.incident_kind === 'si' ||
    group?.incident_kind === 'infra_nuvem' ||
    group?.incident_kind === 'infra_hosting'
      ? group.incident_kind
      : '',
});

export type ReviewIncidentHydrate = {
  workflow_id?: number | '';
  incidents_group_id?: number;
  title?: string;
  comments?: string;
  urgency?: string;
  customFieldValues?: IncidentCustomFieldValues;
  open_in_incidents?: boolean;
  incident_kind?: IncidentGroupDraft['incident_kind'];
  incident_attachment_allowed?: boolean;
};

export function hydrateIncidentGroupDraft(
  group: ChangeWorkGroup | null,
  review?: ReviewIncidentHydrate | null,
): IncidentGroupDraft {
  const base = emptyDraft(group);
  if (!review) return base;
  const workflowId =
    review.workflow_id === undefined
      ? base.incidents_workflow_id
      : review.workflow_id || undefined;
  return {
    ...base,
    open_in_incidents: review.open_in_incidents ?? base.open_in_incidents,
    incidents_group_id: review.incidents_group_id ?? base.incidents_group_id,
    incidents_workflow_id: workflowId,
    incident_title_default: review.title ?? base.incident_title_default,
    incident_description_default: review.comments ?? base.incident_description_default,
    incident_urgency_default: review.urgency ?? base.incident_urgency_default,
    incident_kind: review.incident_kind ?? base.incident_kind,
    incident_attachment_allowed:
      review.incident_attachment_allowed ?? base.incident_attachment_allowed,
    incident_custom_field_defaults:
      review.customFieldValues && Object.keys(review.customFieldValues).length
        ? { ...review.customFieldValues }
        : base.incident_custom_field_defaults,
  };
}

const ChangeWorkGroupIncidentModal: React.FC<Props> = ({
  group,
  targets,
  saving,
  onHide,
  onSave,
  users = [],
  embedded = false,
  initialDraft = null,
  initialCustomFieldValues,
  initialCustomFields,
  onDraftChange,
  hideIncidentKind = false,
}) => {
  const [draft, setDraft] = useState<IncidentGroupDraft>(
    () => initialDraft || emptyDraft(group),
  );
  const [error, setError] = useState<string | null>(null);
  const [customFields, setCustomFields] = useState<WorkflowCustomField[]>(
    () => initialCustomFields || [],
  );
  const [customFieldValues, setCustomFieldValues] = useState<IncidentCustomFieldValues>(() => {
    if (initialCustomFieldValues && Object.keys(initialCustomFieldValues).length) {
      return initialCustomFieldValues;
    }
    return hydrateIncidentCustomFieldValues(
      (initialDraft || emptyDraft(group)).incident_custom_field_defaults,
    );
  });
  const [fieldsLoaded, setFieldsLoaded] = useState(false);
  const { groups: contactGroups, loading: groupsLoading } = useIncidentFirstContactGroups(
    draft.incidents_workflow_id,
  );
  const {
    targets: loadedTargets,
    loading: loadingTargets,
    error: targetsError,
  } = useIncidentTargets(Boolean(group));
  const workflows = loadedTargets.workflows.length ? loadedTargets.workflows : targets.workflows;

  useEffect(() => {
    if (embedded) return;
    const next = emptyDraft(group);
    setDraft(next);
    setCustomFields([]);
    setCustomFieldValues(hydrateIncidentCustomFieldValues(next.incident_custom_field_defaults));
    setFieldsLoaded(false);
    setError(null);
  }, [embedded, group]);

  useEffect(() => {
    if (!draft.incidents_workflow_id || groupsLoading) return;
    if (!contactGroups.length) return;
    const nextGroup = pickFirstContactGroup(
      contactGroups,
      group?.incidents_group_id,
      group?.name || undefined,
    );
    setDraft((prev) => {
      if (prev.incidents_group_id && contactGroups.some((item) => item.id === prev.incidents_group_id)) {
        return prev;
      }
      if (prev.incidents_group_id === nextGroup) return prev;
      return { ...prev, incidents_group_id: nextGroup };
    });
  }, [
    contactGroups,
    draft.incidents_workflow_id,
    group?.incidents_group_id,
    group?.name,
    groupsLoading,
  ]);

  const applyWorkflow = (workflowId: number | undefined) => {
    if (workflowId === draft.incidents_workflow_id) return;
    const keepDefaults =
      workflowId && workflowId === (group?.incidents_workflow_id || undefined)
        ? hydrateIncidentCustomFieldValues(group?.incident_custom_field_defaults)
        : {};
    setDraft((prev) => ({
      ...prev,
      incidents_workflow_id: workflowId,
      incidents_group_id: undefined,
      incident_custom_field_defaults: keepDefaults,
    }));
    setCustomFieldValues(keepDefaults);
    setFieldsLoaded(false);
  };

  const handleSave = async () => {
    if (draft.open_in_incidents && !draft.incidents_workflow_id) {
      setError('Escolha o fluxo de trabalho para ver as abas da solicitação.');
      return;
    }
    if (draft.open_in_incidents && !draft.incidents_group_id) {
      setError('Escolha o grupo para encaminhar o chamado.');
      return;
    }
    if (draft.open_in_incidents && draft.incidents_workflow_id && !fieldsLoaded) {
      setError('Aguarde as informações da solicitação deste fluxo.');
      return;
    }
    if (draft.open_in_incidents && draft.incidents_workflow_id) {
      const visibleFields = customFields.filter(
        (field) => field.field_type !== 'file' && field.field_type !== 'files',
      );
      const customError = validateIncidentTicketFields(visibleFields, customFieldValues, {});
      if (customError) {
        setError(customError);
        return;
      }
    }
    setError(null);
    await onSave({
      ...draft,
      prompt_on_create: draft.open_in_incidents,
      incident_attachment_allowed: draft.incident_attachment_allowed,
      incident_custom_field_defaults: draft.open_in_incidents
        ? serializeIncidentCustomFieldDefaults(customFields, customFieldValues)
        : {},
    });
  };

  const areaName = group?.name || '';

  useEffect(() => {
    if (!embedded) return;
    onDraftChange?.(draft, { customFieldValues, customFields });
    // onDraftChange é o pai; não entra nas deps para não ciclar o render.
  }, [customFieldValues, customFields, draft, embedded]);

  const form = (
    <>
        <p className="small text-muted mb-3">
          Mesma ordem do Novo Ticket: escolha o fluxo para abrir as abas da solicitação. Depois
          escolha o grupo para encaminhar. Na criação e no corpo da mudança você revisa, edita e
          envia.
        </p>
        {error ? (
          <Alert variant="warning" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {loadingTargets ? (
          <Alert variant="info">Carregando fluxos de Incidentes...</Alert>
        ) : null}
        {!loadingTargets && !workflows.length ? (
          <Alert variant="warning">
            {targetsError ||
              'Nenhum catálogo de Incidentes neste backend. Sem Compras/Contratação.'}
          </Alert>
        ) : null}
        <Form.Check
          type="switch"
          id={`open-incident-${group?.group_id || 'draft'}`}
          className="mb-2"
          label="Abre chamado no Incidente"
          checked={draft.open_in_incidents}
          onChange={() =>
            setDraft((prev) => ({ ...prev, open_in_incidents: !prev.open_in_incidents }))
          }
        />
        {draft.open_in_incidents ? (
          <>
            {hideIncidentKind ? null : (
              <>
                <Form.Label htmlFor={`incident-kind-${group?.group_id || 'draft'}`}>
                  Papel deste grupo
                </Form.Label>
                <Form.Select
                  id={`incident-kind-${group?.group_id || 'draft'}`}
                  className="mb-3"
                  value={draft.incident_kind}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      incident_kind: e.target.value as IncidentGroupDraft['incident_kind'],
                    }))
                  }
                >
                  <option value="">Sem papel específico</option>
                  <option value="si">Avaliação SI</option>
                  <option value="infra_nuvem">Infra Nuvem</option>
                  <option value="infra_hosting">Infra Hosting</option>
                </Form.Select>
                <Form.Text className="text-muted d-block mb-3">
                  Avaliação SI trava Autorizar. Infra Nuvem/Hosting podem abrir chamado manualmente,
                  conforme SAP ou Não SAP da mudança.
                </Form.Text>
              </>
            )}
            <Form.Check
              type="switch"
              id={`attach-allowed-${group?.group_id || 'draft'}`}
              className="mb-3"
              label="Permitir anexos neste chamado"
              checked={draft.incident_attachment_allowed}
              onChange={() =>
                setDraft((prev) => ({
                  ...prev,
                  incident_attachment_allowed: !prev.incident_attachment_allowed,
                }))
              }
            />
            <IncidentTicketFieldsForm
              workflowId={draft.incidents_workflow_id || ''}
              incidentsGroupId={draft.incidents_group_id}
              values={customFieldValues}
              files={{}}
              initialValues={customFieldValues}
              hideFileFields
              ariaPrefix={`Solicitação de ${areaName || 'grupo'}`}
              users={users}
              onValuesChange={setCustomFieldValues}
              onFilesChange={() => undefined}
              onFieldsChange={(fields) => {
                setCustomFields(fields);
                setCustomFieldValues((prev) => pickIncidentFieldValues(prev, fields));
                setFieldsLoaded(Boolean(draft.incidents_workflow_id));
              }}
              infoTab={
                <IncidentNovoTicketInfoFields
                  name={areaName}
                  workflowId={draft.incidents_workflow_id || ''}
                  workflows={workflows}
                  workflowsLoading={loadingTargets}
                  onWorkflowChange={(workflowId) => applyWorkflow(workflowId || undefined)}
                  groupId={draft.incidents_group_id}
                  groups={contactGroups}
                  groupsLoading={groupsLoading}
                  onGroupChange={(queueId) =>
                    setDraft((prev) => ({ ...prev, incidents_group_id: queueId }))
                  }
                  urgency={draft.incident_urgency_default}
                  onUrgencyChange={(value) =>
                    setDraft((prev) => ({ ...prev, incident_urgency_default: value }))
                  }
                  title={draft.incident_title_default}
                  onTitleChange={(value) =>
                    setDraft((prev) => ({ ...prev, incident_title_default: value }))
                  }
                  description={draft.incident_description_default}
                  onDescriptionChange={(value) =>
                    setDraft((prev) => ({ ...prev, incident_description_default: value }))
                  }
                  titleLabel="Título padrão"
                  descriptionLabel="Descrição padrão"
                  urgencyEmptyLabel="Sem urgência padrão"
                />
              }
            />
          </>
        ) : null}
  </>
  );

  if (embedded) {
    return <div>{form}</div>;
  }

  return (
    <Modal
      show={Boolean(group)}
      onHide={onHide}
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton={false}>
        <Modal.Title>Chamado no Incidente — {group?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>{form}</Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
          Cancelar
        </Button>
        <Button onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangeWorkGroupIncidentModal;
